import React, { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useSessionStore } from '../../store/sessionStore';
import { BASE_VB, ViewBox } from '../../types/index';
import RingLayer   from './RingLayer';
import NodeLayer   from './NodeLayer';
import ThreadLayer from './ThreadLayer';
import ImpactLayer from './ImpactLayer';
import CursorLayer from './CursorLayer';
import { buildEdgeMap, deriveProcessNodesFromEdges, resolveProcessEdges } from '../../utils/processEdges';

interface CanvasProps {
  svgRef?: React.RefObject<SVGSVGElement | null>;
  onSegmentClick:     (procId: number, segIdx: number) => void;
  onSegmentHover?:    (procId: number, segIdx: number, hovered: boolean) => void;
  hoveredSegmentKey?: string | null;
  onNodeDblClick:     (nodeId: string, clientX: number, clientY: number) => void;
  onCanvasRightClick: (svgX: number, svgY: number, clientX: number, clientY: number) => void;
}

const Canvas: React.FC<CanvasProps> = ({
  svgRef: externalSvgRef,
  onSegmentClick,
  onSegmentHover,
  hoveredSegmentKey: externalHoveredSegmentKey = null,
  onNodeDblClick,
  onCanvasRightClick,
}) => {
  const localSvgRef = useRef<SVGSVGElement>(null);
  const svgRef = externalSvgRef ?? localSvgRef;

  const nodes           = useSessionStore(s => s.nodes);
  const processes       = useSessionStore(s => s.processes);
  const edges           = useSessionStore(s => s.edges);
  const viewBox         = useSessionStore(s => s.viewBox);
  const ringScale       = useSessionStore(s => s.ringScale);
  const selectedNodeId  = useSessionStore(s => s.selectedNodeId);
  const activeProcessId = useSessionStore(s => s.activeProcessId);
  const setViewBox      = useSessionStore(s => s.setViewBox);
  const setRingScale    = useSessionStore(s => s.setRingScale);
  const handleNodeClick = useSessionStore(s => s.handleNodeClick);
  const updateNode      = useSessionStore(s => s.updateNode);
  const updateSegment   = useSessionStore(s => s.updateSegment);
  const selectNode      = useSessionStore(s => s.selectNode);
  const setHint         = useSessionStore(s => s.setHint);
  const edgeMap         = useMemo(() => buildEdgeMap(edges), [edges]);

  // ── Coords ──────────────────────────────────────────────────────────────────

  const svgPt = useCallback((clientX: number, clientY: number): [number, number] => {
    const svg = svgRef.current;
    if (!svg) return [0, 0];
    const r = svg.getBoundingClientRect();
    const v = viewBox;
    return [
      (clientX - r.left) * (v.w / r.width)  + v.x,
      (clientY - r.top)  * (v.h / r.height) + v.y,
    ];
  }, [viewBox, svgRef]);

  // ── Zoom ────────────────────────────────────────────────────────────────────

  const zoomAt = useCallback((cx: number, cy: number, factor: number) => {
    setViewBox((prev: ViewBox) => {
      let { x, y, w, h } = prev;
      x = cx - (cx - x) / factor;
      y = cy - (cy - y) / factor;
      w /= factor;
      h /= factor;
      const scale = BASE_VB.w / w;
      if (scale < 0.2) { w = BASE_VB.w / 0.2; h = BASE_VB.h / 0.2; }
      if (scale > 5)   { w = BASE_VB.w / 5;   h = BASE_VB.h / 5;   }
      return { x, y, w, h } as ViewBox;
    });
  }, [setViewBox]);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      const [sx, sy] = svgPt(e.clientX, e.clientY);
      zoomAt(sx, sy, e.deltaY < 0 ? 1.12 : 1 / 1.12);
    };
    svg.addEventListener('wheel', handler, { passive: false });
    return () => svg.removeEventListener('wheel', handler);
  }, [svgPt, zoomAt, svgRef]);

  // ── Pan ─────────────────────────────────────────────────────────────────────

  const isPanning  = useRef(false);
  const panStart   = useRef({ x: 0, y: 0 });
  const panVbStart = useRef<ViewBox>({ ...BASE_VB });

  const handleSvgMouseDown = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (e.button !== 0) return;
    const target = e.target as Element;
    if (target.closest('.node-g')) return;
    isPanning.current  = true;
    panStart.current   = { x: e.clientX, y: e.clientY };
    panVbStart.current = { ...viewBox };
    e.currentTarget.style.cursor = 'grabbing';
  }, [viewBox]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!isPanning.current) return;
      const svg = svgRef.current;
      if (!svg) return;
      const r  = svg.getBoundingClientRect();
      const vb = panVbStart.current;
      setViewBox({
        ...vb,
        x: vb.x - (e.clientX - panStart.current.x) * (vb.w / r.width),
        y: vb.y - (e.clientY - panStart.current.y) * (vb.h / r.height),
      });
    };
    const onUp = () => {
      if (!isPanning.current) return;
      isPanning.current = false;
      if (svgRef.current) svgRef.current.style.cursor = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
    };
  }, [setViewBox, svgRef]);

  // ── Drag de nodos ────────────────────────────────────────────────────────────

  const draggingId  = useRef<string | null>(null);
  const dragOffset  = useRef<[number, number]>([0, 0]);
  const wasDragging = useRef(false);

  const handleNodeMouseDown = useCallback((e: React.MouseEvent, id: string) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    const [sx, sy] = svgPt(e.clientX, e.clientY);
    const n = nodes[id];
    if (!n) return;
    draggingId.current  = id;
    dragOffset.current  = [sx - n.x, sy - n.y];
    wasDragging.current = false;
  }, [svgPt, nodes]);

  // ── Rubber-band ──────────────────────────────────────────────────────────────

  const [rubberBand, setRubberBand] = useState<{
    x1: number; y1: number; x2: number; y2: number; color: string;
  } | null>(null);
  const [endpointDrag, setEndpointDrag] = useState<{
    procId: number;
    segIdx: number;
    endpoint: 'from' | 'to';
    x: number;
    y: number;
  } | null>(null);
  const [hoveredSegmentKey, setHoveredSegmentKey] = useState<string | null>(null);
  const effectiveHoveredSegmentKey = externalHoveredSegmentKey ?? hoveredSegmentKey;
  const [isPlaybackMode, setIsPlaybackMode] = useState(false);
  const [playbackCursor, setPlaybackCursor] = useState(0);

  const centerViewAt = useCallback((x: number, y: number) => {
    setViewBox(prev => ({
      ...prev,
      x: x - prev.w / 2,
      y: y - prev.h / 2,
    }));
  }, [setViewBox]);

  const handleSvgMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    const [sx, sy] = svgPt(e.clientX, e.clientY);

    if (endpointDrag) {
      setEndpointDrag(prev => (prev ? { ...prev, x: sx, y: sy } : null));
      return;
    }

    if (draggingId.current) {
      const [ox, oy] = dragOffset.current;
      const nx = sx - ox;
      const ny = sy - oy;
      const curr = nodes[draggingId.current];
      if (!curr) return;
      if (Math.abs(nx - curr.x) > 2 || Math.abs(ny - curr.y) > 2) wasDragging.current = true;
      updateNode(draggingId.current, { x: nx, y: ny });
      return;
    }

    const proc    = processes.find(p => p.id === activeProcessId);
    const selNode = selectedNodeId ? nodes[selectedNodeId] : null;
    if (proc && selNode) {
      setRubberBand({ x1: selNode.x, y1: selNode.y, x2: sx, y2: sy, color: proc.color });
    } else {
      setRubberBand(null);
    }
  }, [svgPt, nodes, updateNode, processes, activeProcessId, selectedNodeId, endpointDrag]);

  const handleSvgMouseUp = useCallback(() => {
    draggingId.current = null;
    if (!endpointDrag) return;

    const { procId, segIdx, endpoint, x, y } = endpointDrag;
    const targetNode = Object.values(nodes).find(n => Math.hypot(n.x - x, n.y - y) <= 16);
    if (!targetNode) {
      setHint('Suelta sobre un nodo para cambiar origen/destino');
      setEndpointDrag(null);
      return;
    }
    const proc = processes.find(p => p.id === procId);
    const seg = proc ? resolveProcessEdges(proc, edgeMap)[segIdx] : undefined;
    if (!proc || !seg) {
      setEndpointDrag(null);
      return;
    }
    if (endpoint === 'from') {
      updateSegment(procId, segIdx, { from: targetNode.id });
      setHint(`Origen actualizado → ${targetNode.label}`);
    } else {
      updateSegment(procId, segIdx, { to: targetNode.id });
      setHint(`Destino actualizado → ${targetNode.label}`);
    }
    setEndpointDrag(null);
  }, [endpointDrag, nodes, processes, updateSegment, setHint, edgeMap]);

  // ── Clic derecho ─────────────────────────────────────────────────────────────

  const handleContextMenu = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    e.preventDefault();
    const [sx, sy] = svgPt(e.clientX, e.clientY);
    onCanvasRightClick(sx, sy, e.clientX, e.clientY);
  }, [svgPt, onCanvasRightClick]);

  // ── Clic en fondo (deselecciona) ─────────────────────────────────────────────

  const handleSvgClick = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    const target = e.target as Element;
    if (!target.closest('.node-g')) {
      selectNode(null);
      setRubberBand(null);
    }
  }, [selectNode]);

  const handleThreadEndpointMouseDown = useCallback((
    e: React.MouseEvent<SVGCircleElement>,
    procId: number,
    segIdx: number,
    endpoint: 'from' | 'to',
    point: { x: number; y: number },
  ) => {
    e.stopPropagation();
    e.preventDefault();
    setEndpointDrag({
      procId,
      segIdx,
      endpoint,
      x: point.x,
      y: point.y,
    });
    setHint(endpoint === 'from'
      ? 'Arrastra y suelta sobre un nodo para cambiar el origen'
      : 'Arrastra y suelta sobre un nodo para cambiar el destino');
  }, [setHint]);

  // ── Nodos in-flow ────────────────────────────────────────────────────────────

  const inFlowNodes: Record<string, string> = {};
  processes.forEach(proc => {
    if (proc.visible === false) return;
    deriveProcessNodesFromEdges(resolveProcessEdges(proc, edgeMap)).forEach(n => { inFlowNodes[n.id] = proc.color; });
  });
  const playbackSequence = processes
    .filter(proc => proc.visible !== false)
    .flatMap(proc => resolveProcessEdges(proc, edgeMap).map((_, segIdx) => ({ procId: proc.id, segIdx })));
  const playbackSegmentState = isPlaybackMode
    ? playbackSequence.reduce<Record<string, boolean>>((acc, step, index) => {
      acc[`${step.procId}:${step.segIdx}`] = index < playbackCursor;
      return acc;
    }, {})
    : null;
  const playbackNodeState = isPlaybackMode
    ? playbackSequence.reduce<Record<string, boolean>>((acc, step, index) => {
      const proc = processes.find(item => item.id === step.procId);
      const seg = proc ? resolveProcessEdges(proc, edgeMap)[step.segIdx] : undefined;
      if (!seg) return acc;
      if (index < playbackCursor) {
        acc[seg.from] = true;
        acc[seg.to] = true;
      }
      return acc;
    }, {})
    : null;
  const playbackTotal = playbackSequence.length;

  const zoomPct = Math.round((BASE_VB.w / viewBox.w) * 100);
  const vbStr   = `${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`;

  return (
    <div style={{ flex: 1, minWidth: 0, minHeight: 0, height: '100vh',  position: 'relative', overflow: 'auto',  }}>
      <svg
        ref={svgRef}
        viewBox={vbStr}
        xmlns="http://www.w3.org/2000/svg"
        style={{ width: '100%', height: '100%', display: 'block' }}
        onMouseDown={handleSvgMouseDown}
        onMouseMove={handleSvgMouseMove}
        onMouseUp={handleSvgMouseUp}
        onClick={handleSvgClick}
        onContextMenu={handleContextMenu}
      >
        <RingLayer scale={ringScale} />

        <ThreadLayer
          processes={processes}
          edges={edges}
          nodes={nodes}
          onSegmentClick={onSegmentClick}
          onEndpointMouseDown={handleThreadEndpointMouseDown}
          draggingEndpointKey={endpointDrag ? `${endpointDrag.procId}:${endpointDrag.segIdx}` : null}
          dragPreview={endpointDrag}
          hoveredSegmentKey={effectiveHoveredSegmentKey}
          onSegmentHover={(procId, segIdx, hovered) => {
            setHoveredSegmentKey(hovered ? `${procId}:${segIdx}` : null);
            onSegmentHover?.(procId, segIdx, hovered);
          }}
          playbackSegmentState={playbackSegmentState}
        />

        <ImpactLayer processes={processes} edges={edges} nodes={nodes} />

        <NodeLayer
          nodes={nodes}
          processes={processes}
          edges={edges}
          selectedNodeId={selectedNodeId}
          inFlowNodes={inFlowNodes}
          onMouseDown={handleNodeMouseDown}
          onClick={(e, id) => {
            e.stopPropagation();
            if (wasDragging.current) { wasDragging.current = false; return; }
            const node = nodes[id];
            if (node) centerViewAt(node.x, node.y);
            handleNodeClick(id);
          }}
          onDblClick={(e, id) => {
            e.stopPropagation();
            if (nodes[id]?.subtype !== 'person') {
              onNodeDblClick(id, e.clientX, e.clientY);
            }
          }}
          playbackNodeState={playbackNodeState}
        />

        {rubberBand && (
          <line
            x1={rubberBand.x1} y1={rubberBand.y1}
            x2={rubberBand.x2} y2={rubberBand.y2}
            stroke={rubberBand.color} strokeWidth={2}
            strokeDasharray="5,4" strokeLinecap="round"
            opacity={0.5} pointerEvents="none"
          />
        )}
        <CursorLayer />

        <g style={{ pointerEvents: 'none' }}>
          <text x={-518} y={418}
            fontFamily="'Caveat',cursive" fontSize={13} fontWeight={700}
            fill="#c8c2b4" opacity={0.8} style={{ userSelect: 'none' }}>
            Anemonautas · Process Mapper
          </text>
          <text x={-518} y={432}
            fontFamily="'DM Mono',monospace" fontSize={9}
            fill="#c8c2b4" opacity={0.6} style={{ userSelect: 'none' }}>
            anemonautas.eu
          </text>
        </g>
      </svg>

      {/* Zoom controls */}
      <div style={{ position: 'absolute', bottom: 16, right: 16, display: 'flex', flexDirection: 'column', gap: 4, zIndex: 50 }}>
        {[
          { label: '+', title: 'Zoom in',  fn: () => zoomAt(viewBox.x + viewBox.w / 2, viewBox.y + viewBox.h / 2, 1.3) },
          { label: '−', title: 'Zoom out', fn: () => zoomAt(viewBox.x + viewBox.w / 2, viewBox.y + viewBox.h / 2, 1 / 1.3) },
          { label: '⊙', title: 'Reset',    fn: () => setViewBox({ ...BASE_VB }), small: true },
        ].map(z => (
          <button key={z.label} title={z.title} onClick={z.fn} style={{
            width: 32, height: 32, background: 'white', border: '1px solid #ddd',
            borderRadius: 8, fontSize: z.small ? 11 : 16, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 1px 6px rgba(0,0,0,0.1)', color: '#555',
            fontFamily: "'DM Mono',monospace", lineHeight: '1',
          }}>
            {z.label}
          </button>
        ))}
        <div style={{ textAlign: 'center', fontSize: 9, color: '#aaa', fontFamily: "'DM Mono',monospace" }}>
          {zoomPct}%
        </div>
      </div>


      <div   style={{
    position: 'absolute',
    bottom: 16,
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    zIndex: 50,
    width: 'fit-content',
    padding: '6px 8px',
    borderRadius: 10,
    border: '1px solid #e8e1d5',
    background: 'rgba(255,255,255,0.92)',
  }}>
             {!isPlaybackMode && 
        <button
          type="button"
          onClick={() => {
            setIsPlaybackMode(prev => {
              const next = !prev;
              setPlaybackCursor(0);
              return next;
            });
          }}
          style={{
            minWidth: 52,
            height: 28,
            padding: '0 10px',
            borderRadius: 7,
            border: isPlaybackMode ? '1px solid #4a7fb5' : '1px solid #ddd',
            background: isPlaybackMode ? '#eaf1fb' : 'white',
            color: isPlaybackMode ? '#355f8a' : '#555',
            cursor: 'pointer',
            fontFamily: "'DM Mono',monospace",
            fontSize: 11,
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          }}
          title="Modo presentación por pasos"
        >
  
          ▶ Play

        </button>
           }

        {isPlaybackMode && (
          <>

            <button
              type="button"
              onClick={() => setPlaybackCursor(prev => Math.max(prev - 1, 0))}
              disabled={playbackCursor === 0  }
              style={{
                 minWidth: 52,
                height: 28,
                padding: '0 10px',
                borderRadius: 7,
                border: '1px solid #ddd',
                background: playbackCursor === 0 ? '#f6f6f6' : 'white',
                color: playbackCursor === 0  ? '#b7b7b7' : '#555',
                cursor: playbackCursor === 0 ? 'not-allowed' : 'pointer',
                fontFamily: "'DM Mono',monospace",
                fontSize: 11,
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              }}
              title="Mostrar el siguiente step"
            >
              Back
            </button>
        <button
          type="button"
          onClick={() => {
            setIsPlaybackMode(prev => {
              const next = !prev;
              setPlaybackCursor(0);
              return next;
            });
          }}
          style={{
            minWidth: 52,
            height: 28,
            padding: '0 10px',
            borderRadius: 7,
            border: isPlaybackMode ? '1px solid #4a7fb5' : '1px solid #ddd',
            background: isPlaybackMode ? '#eaf1fb' : 'white',
            color: isPlaybackMode ? '#355f8a' : '#555',
            cursor: 'pointer',
            fontFamily: "'DM Mono',monospace",
            fontSize: 11,
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          }}
          title="Modo presentación por pasos"
        >
           ⏹️ Stop
        </button>
            <button
              type="button"
              onClick={() => setPlaybackCursor(prev => Math.min(prev + 1, playbackTotal))}
              disabled={playbackTotal === 0 || playbackCursor >= playbackTotal}
              style={{
                minWidth: 52,
                height: 28,
                                visibility: playbackTotal === 0 || playbackCursor >= playbackTotal  ? 'hidden' : 'visible',

                padding: '0 10px',
                borderRadius: 7,
                border: '1px solid #ddd',
                background: playbackTotal === 0 || playbackCursor >= playbackTotal ? '#f6f6f6' : 'white',
                color: playbackTotal === 0 || playbackCursor >= playbackTotal ? '#b7b7b7' : '#555',
                cursor: playbackTotal === 0 || playbackCursor >= playbackTotal ? 'not-allowed' : 'pointer',
                fontFamily: "'DM Mono',monospace",
                fontSize: 11,
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              }}
              title="Mostrar el siguiente step"
            >
              Next
            </button>
            <div style={{ alignSelf: 'center', fontSize: 10, color: '#8e8679', fontFamily: "'DM Mono',monospace" }}>
              {Math.min(playbackCursor, playbackTotal)}/{playbackTotal}
            </div>
          </>
        )} 
      </div>


      <div style={{
        position: 'absolute',
        bottom: 16,
        right: 16,
        display: 'flex',
        gap: 6,
        zIndex: 50,
        padding: '6px 8px',
        borderRadius: 10,
        border: '1px solid #e8e1d5',
        background: 'rgba(255,255,255,0.92)',
      }}>
 
        {[1.5, 2, 3].map(scale => (
          <button
            key={scale}
            type="button"
            title={`Tamaño decágonos x${scale}`}
            onClick={() => setRingScale(scale)}
            style={{
              minWidth: 36,
              height: 28,
              padding: '0 8px',
              borderRadius: 7,
              border: ringScale === scale ? '1px solid #4a9e6b' : '1px solid #ddd',
              background: ringScale === scale ? '#eaf7ef' : 'white',
              color: ringScale === scale ? '#2d7a50' : '#555',
              cursor: 'pointer',
              fontFamily: "'DM Mono',monospace",
              fontSize: 11,
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            }}
          >
            x{scale}
          </button>
        ))}
      </div>


    </div>
  );
};

export default Canvas;
